import { Avatar, Badge, Button, Stack, Switch, Table } from "@mantine/core";
import { useListState } from "@mantine/hooks";
import { openContextModal } from "@mantine/modals";
import { IconGripVertical, IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  DropResult,
  Droppable,
} from "react-beautiful-dnd";
import { CircleFlag } from "react-circle-flags";
import { getShowClubs, getShowCountries } from "../../common/util";
import {
  deleteEventRegistration,
  updateEventRegistration,
} from "../../common/api";
import { useIsBoardAdmin } from "../../common/boards/board.hooks";
import { Member } from "../../common/members/member.schema";
import {
  Registration,
  SeedType,
  Event,
} from "../../common/events/event.schema";
import MemberDisplay from "../members/MemberDisplay";
import UserDisplay from "../users/UserDisplay";
import { getIsTournament } from "./util";
import { trackEvent } from "../../common/analytics";
const ORDER_SPACE = 100;
const RegistrationDisplay = ({
  registration,
  event,
  dragHandleProps,
}: {
  registration: Registration;
  event: Event;
  dragHandleProps?: DraggableProvidedDragHandleProps;
}) => {
  const { t } = useTranslation();
  const [approved, setApproved] = useState(registration.approved);
  const [checkedIn, setCheckedIn] = useState(registration.checkedIn);
  const [paid, setPaid] = useState(registration.paid);
  const queryClient = useQueryClient();
  const onChangeApproved = (approved: boolean) => {
    setApproved(approved);
    mutate({
      approved,
      registrationId: registration._id,
      eventId: event._id,
    });
  };
  const onChangePaid = (paid: boolean) => {
    setPaid(paid);
    mutate({
      paid,
      registrationId: registration._id,
      eventId: event._id,
    });
  };
  const onChangeCheckedIn = (checkedIn: boolean) => {
    setCheckedIn(checkedIn);
    mutate({
      checkedIn,
      registrationId: registration._id,
      eventId: event._id,
    });
  };
  const { mutate } = useMutation(updateEventRegistration, {
    onSuccess: () => {
      queryClient.invalidateQueries(["event", event.shortLink]);
    },
  });
  const { mutate: deleteRegistration } = useMutation(deleteEventRegistration, {
    onSuccess: () => {
      queryClient.invalidateQueries(["event", event.shortLink]);
    },
  });
  const onClickRemove = () => {
    deleteRegistration({
      registrationId: registration._id,
      eventId: event._id,
    });
  };
  const index = event.registrations.indexOf(registration);
  const member = (event.members as Member[]).find(
    (m) => m._id === registration.member
  );

  // console.log("event.registrations :", event.registrations);
  const showClubs = getShowClubs(event.registrations);
  const showCountries = getShowCountries(event.registrations);
  const isTournament = getIsTournament(event);
  // console.log("showCountries :", showCountries);
  return (
    <>
      {event.seedType === SeedType.MANUAL && (
        <td
          {...dragHandleProps}
          //  className={classes.dragHandle}
        >
          <IconGripVertical size="1.05rem" stroke={1.5} />
        </td>
      )}
      {isTournament && (
        <td>
          <Avatar color="blue" size="sm">
            {index + 1}
          </Avatar>
        </td>
      )}
      <td>
        {registration.user && (
          <UserDisplay user={registration.user} size="sm" />
        )}
        {!registration.user && registration.member && (
          <MemberDisplay avatar member={registration.member} size="sm" />
        )}
      </td>
      {showCountries && (
        <td>
          {registration.country && (
            <CircleFlag
              countryCode={registration.country.toLowerCase()}
              height={30}
            />
          )}
        </td>
      )}
      {showClubs && <td>{registration.club}</td>}
      {isTournament && event.seedType === SeedType.SCORE && (
        <td>
          <Avatar color="gray" size="sm">
            {member ? member.score : 500}
          </Avatar>
        </td>
      )}
      {event.seedType === SeedType.USFA && (
        <td>
          <Avatar color="gray" size="sm">
            {registration.usfaRating}
          </Avatar>
        </td>
      )}
      <td width={100}>
        <Stack>
          {!registration.member && <Badge size="xs">{t("newUser")}</Badge>}
          <Switch
            size="xs"
            label="approved"
            onChange={(e) => onChangeApproved(e.target.checked)}
            checked={approved}
          />
          {event.requireRegistrationPayment && (
            <Switch
              size="xs"
              label="paid"
              onChange={(e) => onChangePaid(e.target.checked)}
              checked={paid}
            />
          )}
          {event.requireCheckIn && registration.user && (
            <Switch
              size="xs"
              label="checked in"
              onChange={(e) => onChangeCheckedIn(e.target.checked)}
              checked={checkedIn}
            />
          )}
          <Button
            size="xs"
            variant="subtle"
            onClick={() => onClickRemove()}
            leftSection={<IconTrash size={15} />}
          >
            {t("remove")}
          </Button>
        </Stack>
      </td>
    </>
  );
};
const RegistrationsAdmin = ({ event }: { event: Event }) => {
  const [registrations, handlers] = useListState(event.registrations);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const members = event.members as Member[];
  const { mutate } = useMutation(updateEventRegistration, {
    onSuccess: () => {
      queryClient.invalidateQueries(["event", event.shortLink]);
    },
  });
  useEffect(() => {
    handlers.setState(event.registrations);
  }, [handlers, event]);
  registrations.sort((a, b) => a.order - b.order);
  if (event.seedType === SeedType.SCORE) {
    registrations.sort((a, b) => {
      const aMember = members.find((m) => m._id === a.member);
      const bMember = members.find((m) => m._id === b.member);
      const aScore = aMember?.score || 500;
      const bScore = bMember?.score || 500;
      return bScore - aScore;
    });
  }
  const items = registrations.map((registration, index) => {
    if (event.seedType === SeedType.MANUAL)
      return (
        <Draggable
          key={registration._id}
          index={index}
          draggableId={registration._id}
        >
          {(provided) => (
            <Table.Tr
              // mb={10}
              // className={cx(classes.item, {
              //   [classes.itemDragging]: snapshot.isDragging,
              // })}
              ref={provided.innerRef}
              {...provided.draggableProps}
            >
              <RegistrationDisplay
                dragHandleProps={provided.dragHandleProps}
                registration={registration}
                event={event}
              />
            </Table.Tr>
          )}
        </Draggable>
      );
    return (
      <Table.Tr key={registration._id}>
        <RegistrationDisplay
          key={registration._id}
          registration={registration}
          event={event}
        />
      </Table.Tr>
    );
  });
  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;
    const oldIndex = source.index;
    const registration = registrations[oldIndex];
    const otherRegs = registrations.filter((r) => r !== registration);
    const newIndex = destination?.index || 0;
    if (oldIndex === newIndex) return;
    handlers.reorder({ from: oldIndex, to: newIndex });
    const next = otherRegs[newIndex];
    const prev = otherRegs[newIndex - 1];
    if (next) next.order = next.order || 0;
    // console.log("next :", next?.order);
    if (prev) prev.order = prev.order || 0;
    // console.log("prev :", prev?.order);
    let order = 0;
    if (next) order = next.order - ORDER_SPACE;
    if (prev) order = prev.order + ORDER_SPACE;
    if (next && prev) {
      order = prev.order + (next.order - prev.order) / 2;
    }
    // console.log("order :", order);
    registration.order = order;
    mutate({
      registrationId: registration._id,
      order,
      eventId: event._id,
    });
  };
  const atCapacity =
    !!event.maxMembers && registrations.length >= event.maxMembers;
  const isAdmin = useIsBoardAdmin();
  const showAddMember = () => {
    trackEvent("open_modal_add_member", { board: event.board });
    openContextModal({
      title: "Select a member to add",
      innerProps: {
        exclude: event.members,
        event,
        creatable: isAdmin,
      },
      modal: "addEventMember",
    });
  };
  return (
    <Stack>
      <Button
        size="sm"
        variant="default"
        onClick={showAddMember}
        disabled={atCapacity}
      >
        {t("addMember")}
      </Button>
      {event.seedType === SeedType.MANUAL && (
        <Table>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="list" direction="vertical">
              {(provided) => (
                <Table.Tbody
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {items}
                  {provided.placeholder}
                </Table.Tbody>
              )}
            </Droppable>
          </DragDropContext>
        </Table>
      )}
      {event.seedType !== SeedType.MANUAL && (
        <Table>
          <Table.Tbody>{items}</Table.Tbody>
        </Table>
      )}
    </Stack>
  );
};
export default RegistrationsAdmin;
